import { useContext, useEffect } from "react";
import { Navigate, useNavigate, useLocation } from "react-router-dom";
import { STORAGE_KEYS, ROUTES } from "const";
import ShopContext from "context/ShopContext";
import validityExpired from "./validityExpired";

import * as AppSettingsHelper from "helpers/AppSettingsHelper";
import * as TerminalHelper from "helpers/TermialHelper";
import * as SalespersonHelper from "helpers/SalespersonHelper";
import UserHelper from "helpers/UserHelper";

const PrivateRoute = ({ element: RouteComponent, ...rest }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { selectedShop } = useContext(ShopContext);

  const paramsToken = new URLSearchParams(window.location.search).get("token");
  const isLoggedIn = UserHelper.isLoggedIn();

  useEffect(() => {
    if (paramsToken) {
      localStorage.setItem(STORAGE_KEYS.TERMINAL_AUTH_TOKEN, paramsToken);
    }
  }, [paramsToken]);

  useEffect(() => {
    if (!isLoggedIn) return;
    if (selectedShop && validityExpired(selectedShop.subscription_end_date)) {
      if (location.pathname !== ROUTES.SUBSCRIPTION_EXPIRED && location.pathname !== ROUTES.SELECT_SHOP) {
        navigate(ROUTES.SUBSCRIPTION_EXPIRED, { replace: true });
      }
    }
  }, [isLoggedIn, selectedShop, navigate, location.pathname]);

  useEffect(() => {
    if (!isLoggedIn) return;

    const checkGuard = async () => {
      // Routes that bypass POS setup redirects (setup pages, auth, and special state pages) to prevent infinite loops
      const bypassSetupGuard = [
        ROUTES.BILL_NUMBER,
        ROUTES.SELECT_SHOP,
        ROUTES.LOGIN,
        ROUTES.LOCK_SCREEN,
        ROUTES.SUBSCRIPTION_EXPIRED,
        ROUTES.COUNTER,
      ];
      if (bypassSetupGuard.includes(location.pathname)) return;

      const shopId = localStorage.getItem(STORAGE_KEYS.SHOP_ID);
      const terminalId = localStorage.getItem(STORAGE_KEYS.TERMINAL_ID);

      if (!shopId) return navigate(ROUTES.SELECT_SHOP, { replace: true });
      if (!terminalId) return navigate(ROUTES.COUNTER, { replace: true });

      const billDone = await AppSettingsHelper.isBillSetupCompleted();
      if (!billDone) return navigate(ROUTES.BILL_NUMBER, { replace: true });

      const terminalSettings = TerminalHelper.getTerminalSettings();
      const isSalespersonLogin = !!terminalSettings?.salespersonLogin;
      const salesperson = SalespersonHelper.getCurrentSalesperson();

      if (isSalespersonLogin && !salesperson) {
        return navigate(ROUTES.LOCK_SCREEN, { replace: true });
      }

      if (location.pathname === ROUTES.SHIFT) return;

      const shiftId = localStorage.getItem(STORAGE_KEYS.SHIFT_ID);
      if (!shiftId) return navigate(ROUTES.SHIFT, { replace: true });
    };

    checkGuard();
  }, [isLoggedIn, location.pathname, navigate]);

  if (!isLoggedIn) return <Navigate to={ROUTES.LOGIN} replace />;

  return <RouteComponent {...rest} />;
};

export default PrivateRoute;
